import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  TrendingUp,
  Send,
  AlertCircle,
  Wifi,
  WifiOff,
  Settings,
  Save,
  Eye,
  Percent,
  Receipt,
  Search,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { formatCurrency, formatCompactCurrency, formatDate, formatDateTime, cn } from '../../lib/utils';
import {
  useAnypayBalance,
  useAnypayWithdraw,
  useAnypayTransactions,
  useAnypaySync,
} from '../../hooks/useAnypay';
import { useTenantFeeConfig, useUpdateTenantFeeConfig } from '../../hooks/useTenantFeeConfig';
import { useAuthStore } from '../../stores/auth.store';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

type TabType = 'overview' | 'transactions' | 'withdraw' | 'fees';

const statusLabels: Record<string, string> = {
  COMPLETED: 'Confirmado',
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelado',
};

const typeLabels: Record<string, string> = {
  PIX_RECEIVE: 'Pix Recebido',
  PIX_SEND: 'Pix Enviado',
};

const getTypeLabel = (type: string, status: string): string => {
  if (type === 'PIX_RECEIVE' && status === 'PENDING') return 'Pix a Receber';
  if (type === 'PIX_RECEIVE' && status === 'FAILED') return 'Pix Expirado';
  if (type === 'PIX_RECEIVE' && status === 'CANCELLED') return 'Pix Cancelado';
  return typeLabels[type] || type;
};

interface TxDetail {
  id: string;
  type: string;
  status: string;
  grossAmount: number;
  totalFees: number;
  providerFee?: number;
  netAmount: number;
  correlationId: string;
  description?: string;
  customer?: { name: string; document: string; email: string } | null;
  beneficiary?: { name: string; document: string; pixKey: string; pixType: string } | null;
  paidAt?: string;
  endToEndId?: string;
  createdAt: string;
  processedAt?: string;
}

export function AnypayDashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [connectionStatus, setConnectionStatus] = useState<boolean | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTx, setSelectedTx] = useState<TxDetail | null>(null);

  // Withdraw form
  const [withdrawPixKey, setWithdrawPixKey] = useState('');
  const [withdrawPixKeyType, setWithdrawPixKeyType] = useState('cpf');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawDescription, setWithdrawDescription] = useState('');
  const [withdrawName, setWithdrawName] = useState('');
  const [withdrawDocument, setWithdrawDocument] = useState('');
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');

  // Fee config form
  const [feeAdhesion, setFeeAdhesion] = useState('');
  const [feeCashIn, setFeeCashIn] = useState('');
  const [feeCashOut, setFeeCashOut] = useState('');
  const [feeAdhesionEnabled, setFeeAdhesionEnabled] = useState(true);
  const [feeCashInEnabled, setFeeCashInEnabled] = useState(true);
  const [feeCashOutEnabled, setFeeCashOutEnabled] = useState(false);
  const [feeConfigLoaded, setFeeConfigLoaded] = useState(false);
  const [feeSaveSuccess, setFeeSaveSuccess] = useState(false);
  const [feeSaveError, setFeeSaveError] = useState('');
  const [feeFintechPct, setFeeFintechPct] = useState('');
  const [feeProcessorPct, setFeeProcessorPct] = useState('');
  const [feeExemptTypes, setFeeExemptTypes] = useState<string[]>(['EMPLOYEE', 'SUPPLIER']);

  const { user } = useAuthStore();
  const tenantId = user?.tenantId;

  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } = useAnypayBalance();
  const { data: txData, isLoading: txLoading, refetch: refetchTx } = useAnypayTransactions({
    status: statusFilter || undefined,
    limit: 200,
  });
  const withdrawMutation = useAnypayWithdraw();
  const syncMutation = useAnypaySync();
  const [syncResult, setSyncResult] = useState<{ synced: number; skipped: number; failed: number } | null>(null);
  const { data: feeConfig, isLoading: feeConfigLoading } = useTenantFeeConfig(tenantId);
  const updateFeeConfig = useUpdateTenantFeeConfig();

  const transactions = (txData?.transactions || []) as TxDetail[];
  const summary = txData?.summary;

  // Populate fee form when config loads
  if (feeConfig && !feeConfigLoaded) {
    setFeeAdhesion(String(feeConfig.adhesionFee / 100));
    setFeeCashIn(String(feeConfig.cashInPercentage));
    setFeeCashOut(String(feeConfig.cashOutPercentage || 0));
    setFeeAdhesionEnabled(feeConfig.adhesionFeeEnabled);
    setFeeCashInEnabled(feeConfig.cashInFeeEnabled);
    setFeeCashOutEnabled(feeConfig.cashOutFeeEnabled || false);
    setFeeFintechPct(String(feeConfig.fintechPercentage ?? 0.02));
    setFeeProcessorPct(String(feeConfig.processorPercentage ?? 0.03));
    setFeeExemptTypes(feeConfig.membershipFeeExemptTypes ?? ['EMPLOYEE', 'SUPPLIER']);
    setFeeConfigLoaded(true);
  }

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const { data } = await api.get(ENDPOINTS.anypay.testConnection);
      setConnectionStatus(data.connected);
    } catch {
      setConnectionStatus(false);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleRefresh = () => {
    refetchBalance();
    refetchTx();
  };

  const handleSync = async () => {
    setSyncResult(null);
    try {
      const result = await syncMutation.mutateAsync();
      setSyncResult({ synced: result.synced, skipped: result.skipped, failed: result.failed });
      // Auto-refresh after sync
      refetchBalance();
      refetchTx();
    } catch {
      setSyncResult({ synced: 0, skipped: 0, failed: -1 });
    }
  };

  const handleSaveFeeConfig = async () => {
    if (!tenantId) return;
    setFeeSaveError('');
    setFeeSaveSuccess(false);
    try {
      await updateFeeConfig.mutateAsync({
        tenantId,
        dto: {
          adhesionFee: Math.round(parseFloat(feeAdhesion) * 100),
          adhesionFeeEnabled: feeAdhesionEnabled,
          cashInPercentage: parseFloat(feeCashIn),
          cashInFeeEnabled: feeCashInEnabled,
          cashOutPercentage: parseFloat(feeCashOut),
          cashOutFeeEnabled: feeCashOutEnabled,
          fintechPercentage: parseFloat(feeFintechPct),
          processorPercentage: parseFloat(feeProcessorPct),
          membershipFeeExemptTypes: feeExemptTypes,
        },
      });
      setFeeSaveSuccess(true);
      setTimeout(() => setFeeSaveSuccess(false), 3000);
    } catch (err: any) {
      setFeeSaveError(err.response?.data?.message || 'Erro ao salvar configuração de taxas');
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawPixKey || !withdrawAmount || !withdrawName || !withdrawDocument) return;
    setWithdrawError('');
    try {
      await withdrawMutation.mutateAsync({
        pixKey: withdrawPixKey,
        pixKeyType: withdrawPixKeyType,
        amount: parseFloat(withdrawAmount),
        description: withdrawDescription || undefined,
        recipientName: withdrawName,
        recipientDocument: withdrawDocument,
      });
      setWithdrawSuccess(true);
      setWithdrawPixKey('');
      setWithdrawAmount('');
      setWithdrawDescription('');
      setWithdrawName('');
      setWithdrawDocument('');
    } catch (err: any) {
      setWithdrawError(err.response?.data?.message || 'Erro ao solicitar saque');
    }
  };

  // Filter transactions by search
  const filteredTxs = transactions.filter(tx =>
    tx.correlationId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.customer?.document?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.beneficiary?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const confirmedTxs = filteredTxs.filter(tx => tx.status === 'COMPLETED');
  const pendingTxs = filteredTxs.filter(tx => tx.status === 'PENDING');
  const failedTxs = filteredTxs.filter(tx => tx.status === 'FAILED' || tx.status === 'CANCELLED');

  const renderTxRow = (tx: TxDetail) => {
    const isIncoming = tx.type === 'PIX_RECEIVE';
    const amount = tx.grossAmount || tx.netAmount;
    const personName = tx.customer?.name || tx.beneficiary?.name || null;
    return (
      <div
        key={tx.id}
        className="flex items-center justify-between px-4 py-3 hover:bg-surface-800/30 transition-colors cursor-pointer"
        onClick={() => setSelectedTx(tx)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isIncoming ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {isIncoming ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
          </div>
          <div>
            <p className="font-medium text-surface-100 text-sm">{getTypeLabel(tx.type, tx.status)}</p>
            {personName && <p className="text-xs text-surface-300">{personName}</p>}
            <p className="text-xs text-surface-500">{tx.description || tx.correlationId?.slice(0, 20)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className={`font-semibold text-sm ${isIncoming ? 'text-green-400' : 'text-red-400'}`}>
              {isIncoming ? '+' : '-'}{formatCurrency(Number(amount))}
            </p>
            {tx.totalFees > 0 && (
              <p className="text-xs text-yellow-400">Taxa: {formatCurrency(Number(tx.totalFees))}</p>
            )}
            <p className="text-xs text-surface-500">{tx.paidAt ? formatDate(tx.paidAt) : formatDate(tx.createdAt)}</p>
          </div>
          <Badge variant={tx.status === 'COMPLETED' ? 'success' : tx.status === 'PENDING' ? 'warning' : 'error'}>
            {statusLabels[tx.status] || tx.status}
          </Badge>
          <Eye className="w-4 h-4 text-surface-500" />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <Wallet className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">AnyPayBR — Gateway</h1>
            <p className="text-surface-400">Saldo, transações e configuração de taxas</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={handleTestConnection} disabled={testingConnection}>
            {testingConnection ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : connectionStatus === true ? (
              <Wifi className="w-4 h-4 mr-2 text-green-400" />
            ) : connectionStatus === false ? (
              <WifiOff className="w-4 h-4 mr-2 text-red-400" />
            ) : (
              <Wifi className="w-4 h-4 mr-2" />
            )}
            {connectionStatus === true ? 'Conectado' : connectionStatus === false ? 'Desconectado' : 'Testar'}
          </Button>
          <Button variant="ghost" onClick={handleRefresh} disabled={txLoading || balanceLoading}>
            <RefreshCw className={`w-4 h-4 ${(txLoading || balanceLoading) ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncMutation.isPending}
            title="Sincronizar transações do AtivoPay com banco interno (relatórios)"
          >
            {syncMutation.isPending ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {syncMutation.isPending ? 'Sincronizando...' : 'Sync Relatórios'}
          </Button>
        </div>
      </div>

      {/* Sync Result Banner */}
      {syncResult && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className={`rounded-lg p-3 flex items-center gap-3 text-sm ${
            syncResult.failed === -1
              ? 'bg-red-500/10 border border-red-500/30 text-red-400'
              : syncResult.synced > 0
                ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                : 'bg-blue-500/10 border border-blue-500/30 text-blue-400'
          }`}>
            {syncResult.failed === -1 ? (
              <><XCircle className="w-4 h-4" /> Erro ao sincronizar. Tente novamente.</>
            ) : syncResult.synced > 0 ? (
              <><CheckCircle className="w-4 h-4" /> Sincronização concluída: {syncResult.synced} transações sincronizadas, {syncResult.skipped} já existentes{syncResult.failed > 0 ? `, ${syncResult.failed} com erro` : ''}.</>
            ) : (
              <><CheckCircle className="w-4 h-4" /> Tudo sincronizado — nenhuma transação nova encontrada ({syncResult.skipped} já existentes).</>
            )}
            <button onClick={() => setSyncResult(null)} className="ml-auto text-surface-400 hover:text-surface-200">✕</button>
          </div>
        </motion.div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <Card hover glow>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-surface-400">Saldo Escrow</p>
                <p className="text-xl font-bold text-green-400" title={summary ? formatCurrency(summary.escrowBalance) : ''}>
                  {balanceLoading ? '...' : formatCompactCurrency(summary?.escrowBalance ?? balance?.available ?? 0)}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card hover>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-surface-400">Recebido (Pago)</p>
                <p className="text-xl font-bold text-blue-400" title={summary ? formatCurrency(summary.totalReceivedConfirmed) : ''}>
                  {txLoading ? '...' : formatCompactCurrency(summary?.totalReceivedConfirmed ?? 0)}
                </p>
                <p className="text-xs text-surface-500">{summary?.confirmedCount ?? 0} transações</p>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card hover>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Percent className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-surface-400">Taxas</p>
                <p className="text-xl font-bold text-yellow-400" title={summary ? formatCurrency(summary.totalFees) : ''}>
                  {txLoading ? '...' : formatCompactCurrency(summary?.totalFees ?? 0)}
                </p>
                <p className="text-xs text-surface-500">
                  {feeConfig ? `${(feeConfig.cashInPercentage * 100).toFixed(0)}% cash-in` : ''}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card hover>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-surface-400">Pendente</p>
                <p className="text-xl font-bold text-orange-400" title={summary ? formatCurrency(summary.totalPending) : ''}>
                  {txLoading ? '...' : formatCompactCurrency(summary?.totalPending ?? 0)}
                </p>
                <p className="text-xs text-surface-500">{summary?.pendingCount ?? 0} transações</p>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card hover>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-red-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-surface-400">Saques</p>
                <p className="text-xl font-bold text-red-400" title={summary ? formatCurrency(summary.totalWithdrawn ?? 0) : ''}>
                  {txLoading ? '...' : formatCompactCurrency((summary as any)?.totalWithdrawn ?? 0)}
                </p>
                <p className="text-xs text-surface-500">{(summary as any)?.cashOutCount ?? 0} saques</p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 bg-surface-800/50 rounded-lg overflow-x-auto w-full sm:w-fit">
        {[
          { key: 'overview', label: 'Transações', icon: Wallet },
          { key: 'transactions', label: 'Detalhado', icon: Receipt },
          { key: 'withdraw', label: 'Novo Saque', icon: Send },
          { key: 'fees', label: 'Config. Taxas', icon: Settings },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabType)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300',
              activeTab === tab.key
                ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                : 'text-surface-400 hover:text-surface-100 hover:bg-surface-700/50'
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab — grouped by status */}
      {activeTab === 'overview' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {txLoading ? (
            <Card><div className="p-6 space-y-4">{[1,2,3,4,5].map(i => <div key={i} className="animate-pulse bg-surface-800 rounded h-14" />)}</div></Card>
          ) : (
            <>
              {confirmedTxs.length > 0 && (
                <Card>
                  <div className="px-4 py-3 border-b border-surface-800 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="font-semibold text-surface-100">Confirmadas</span>
                    <Badge variant="success">{confirmedTxs.length}</Badge>
                  </div>
                  <div className="divide-y divide-surface-800">{confirmedTxs.slice(0, 10).map(renderTxRow)}</div>
                  {confirmedTxs.length > 10 && (
                    <div className="px-4 py-2 text-center">
                      <Button variant="ghost" size="sm" onClick={() => setActiveTab('transactions')}>
                        Ver todas ({confirmedTxs.length})
                      </Button>
                    </div>
                  )}
                </Card>
              )}
              {pendingTxs.length > 0 && (
                <Card>
                  <div className="px-4 py-3 border-b border-surface-800 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-orange-400" />
                    <span className="font-semibold text-surface-100">Pendentes</span>
                    <Badge variant="warning">{pendingTxs.length}</Badge>
                  </div>
                  <div className="divide-y divide-surface-800">{pendingTxs.slice(0, 10).map(renderTxRow)}</div>
                </Card>
              )}
              {failedTxs.length > 0 && (
                <Card>
                  <div className="px-4 py-3 border-b border-surface-800 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <span className="font-semibold text-surface-100">Falhas / Canceladas</span>
                    <Badge variant="error">{failedTxs.length}</Badge>
                  </div>
                  <div className="divide-y divide-surface-800">{failedTxs.slice(0, 10).map(renderTxRow)}</div>
                </Card>
              )}
              {filteredTxs.length === 0 && (
                <Card><div className="text-center py-8 text-surface-400">Nenhuma transação encontrada</div></Card>
              )}
            </>
          )}
        </motion.div>
      )}

      {/* Detailed Transactions Tab — with search and filters */}
      {activeTab === 'transactions' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <Card>
            <div className="p-4 flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
                <input
                  type="text"
                  placeholder="Buscar por nome, documento, descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-200 placeholder-surface-500 focus:outline-none focus:border-primary-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-200 focus:outline-none focus:border-primary-500"
              >
                <option value="">Todos</option>
                <option value="PAID">Confirmados</option>
                <option value="PENDING">Pendentes</option>
                <option value="FAILED">Falhos</option>
              </select>
            </div>
          </Card>
          <Card>
            {txLoading ? (
              <div className="p-6 space-y-4">{[1,2,3,4,5].map(i => <div key={i} className="animate-pulse bg-surface-800 rounded h-14" />)}</div>
            ) : filteredTxs.length === 0 ? (
              <div className="text-center py-8 text-surface-400">Nenhuma transação encontrada</div>
            ) : (
              <div className="divide-y divide-surface-800 max-h-[600px] overflow-y-auto">
                {filteredTxs.map(renderTxRow)}
              </div>
            )}
          </Card>
        </motion.div>
      )}

      {/* Withdraw Tab */}
      {activeTab === 'withdraw' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {withdrawSuccess ? (
            <Card hover glow>
              <div className="text-center py-8">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </motion.div>
                <h2 className="text-xl font-semibold text-surface-100 mb-2">Saque Solicitado</h2>
                <p className="text-surface-400 mb-6">O saque foi enviado para processamento.</p>
                <Button onClick={() => setWithdrawSuccess(false)}>Novo Saque</Button>
              </div>
            </Card>
          ) : (
            <Card hover glow>
              <h2 className="text-lg font-semibold text-surface-100 mb-4">Solicitar Saque PIX</h2>
              {withdrawError && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span className="text-red-400">{withdrawError}</span>
                </div>
              )}
              <div className="space-y-4 max-w-lg">
                <div>
                  <label className="block text-sm font-medium text-surface-200 mb-2">Tipo de Chave PIX</label>
                  <div className="flex flex-wrap gap-2">
                    {[{v:'cpf',l:'CPF'},{v:'cnpj',l:'CNPJ'},{v:'email',l:'E-mail'},{v:'phone',l:'Telefone'},{v:'random',l:'Aleatória'}].map(t => (
                      <button key={t.v} onClick={() => setWithdrawPixKeyType(t.v)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${withdrawPixKeyType === t.v ? 'bg-primary-500 text-white' : 'bg-surface-800 text-surface-300 hover:bg-surface-700'}`}>
                        {t.l}
                      </button>
                    ))}
                  </div>
                </div>
                <Input label="Chave PIX Destino *" placeholder="CPF, e-mail, telefone ou chave aleatória" value={withdrawPixKey} onChange={(e) => setWithdrawPixKey(e.target.value)} />
                <Input label="Valor (R$) *" type="number" placeholder="0,00" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} min="1" max="10000" step="0.01" />
                <Input label="Nome do Destinatário *" placeholder="Nome completo" value={withdrawName} onChange={(e) => setWithdrawName(e.target.value)} />
                <Input label="CPF/CNPJ do Destinatário *" placeholder="Apenas números" value={withdrawDocument} onChange={(e) => setWithdrawDocument(e.target.value)} />
                <Input label="Descrição (opcional)" placeholder="Ex: Saque receita Finteck" value={withdrawDescription} onChange={(e) => setWithdrawDescription(e.target.value)} />
                <Button onClick={handleWithdraw} disabled={withdrawMutation.isPending || !withdrawPixKey || !withdrawAmount || !withdrawName || !withdrawDocument} className="w-full">
                  {withdrawMutation.isPending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Solicitar Saque
                </Button>
              </div>
            </Card>
          )}
        </motion.div>
      )}

      {/* Fees Config Tab */}
      {activeTab === 'fees' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card hover glow>
            <h2 className="text-lg font-semibold text-surface-100 mb-2">Configuração de Taxas</h2>
            <p className="text-sm text-surface-400 mb-6">Taxas aplicadas sobre recebimentos e envios PIX via AnyPayBR.</p>
            {feeConfigLoading ? (
              <div className="flex items-center justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-surface-400" /></div>
            ) : (
              <div className="space-y-6 max-w-lg">
                {feeSaveSuccess && (
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-green-400">Configuração salva com sucesso</span>
                  </div>
                )}
                {feeSaveError && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <span className="text-red-400">{feeSaveError}</span>
                  </div>
                )}

                {/* Adhesion Fee */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-surface-200">Taxa de Adesão (R$)</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={feeAdhesionEnabled} onChange={(e) => setFeeAdhesionEnabled(e.target.checked)} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500" />
                      <span className="text-xs text-surface-400">Habilitada</span>
                    </label>
                  </div>
                  <Input type="number" placeholder="21.00" value={feeAdhesion} onChange={(e) => setFeeAdhesion(e.target.value)} disabled={!feeAdhesionEnabled} min="0" step="0.01" />
                  <p className="text-xs text-surface-500">Valor fixo descontado de cada recebimento</p>
                </div>

                {/* Cash-in Percentage */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-surface-200">Taxa Cash-in (%)</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={feeCashInEnabled} onChange={(e) => setFeeCashInEnabled(e.target.checked)} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500" />
                      <span className="text-xs text-surface-400">Habilitada</span>
                    </label>
                  </div>
                  <Input type="number" placeholder="5.00" value={feeCashIn} onChange={(e) => setFeeCashIn(e.target.value)} disabled={!feeCashInEnabled} min="0" max="100" step="0.01" />
                  <p className="text-xs text-surface-500">Percentual sobre o valor bruto de cada PIX recebido</p>
                </div>

                {/* Split de Taxa Cash-in */}
                <div className="p-4 rounded-lg bg-surface-800/30 border border-surface-700 space-y-4">
                  <p className="text-sm font-medium text-surface-200">Composição do Split Cash-in</p>
                  <p className="text-xs text-surface-500">Fintech + Processador = Cash-in total ({feeCashIn}%)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-surface-300">Fintech (receita)</label>
                      <Input type="number" placeholder="0.02" value={feeFintechPct} onChange={(e) => setFeeFintechPct(e.target.value)} min="0" max="1" step="0.0001" />
                      <p className="text-xs text-surface-500">{((parseFloat(feeFintechPct || '0')) * 100).toFixed(2)}%</p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-surface-300">Processador (custo)</label>
                      <Input type="number" placeholder="0.03" value={feeProcessorPct} onChange={(e) => setFeeProcessorPct(e.target.value)} min="0" max="1" step="0.0001" />
                      <p className="text-xs text-surface-500">{((parseFloat(feeProcessorPct || '0')) * 100).toFixed(2)}%</p>
                    </div>
                  </div>
                  {feeCashIn && feeFintechPct && feeProcessorPct && (
                    Math.abs((parseFloat(feeFintechPct) + parseFloat(feeProcessorPct)) - parseFloat(feeCashIn)) > 0.0001 ? (
                      <div className="flex items-center gap-2 text-red-400 text-xs">
                        <AlertCircle className="w-4 h-4" />
                        <span>Soma ({((parseFloat(feeFintechPct || '0') + parseFloat(feeProcessorPct || '0')) * 100).toFixed(2)}%) ≠ Cash-in ({(parseFloat(feeCashIn || '0') * 100).toFixed(2)}%)</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-green-400 text-xs">
                        <CheckCircle className="w-4 h-4" />
                        <span>Split válido</span>
                      </div>
                    )
                  )}
                </div>

                {/* Tipos isentos */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-surface-200">Tipos Isentos de Taxa de Adesão</label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {['CUSTOMER', 'EMPLOYEE', 'SUPPLIER'].map(type => (
                      <label key={type} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={feeExemptTypes.includes(type)} onChange={(e) => {
                          if (e.target.checked) setFeeExemptTypes(prev => [...prev, type]);
                          else setFeeExemptTypes(prev => prev.filter(t => t !== type));
                        }} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500" />
                        <span className="text-sm text-surface-300">{type === 'CUSTOMER' ? 'Cliente' : type === 'EMPLOYEE' ? 'Funcionário' : 'Fornecedor'}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Cash-out */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-surface-200">Taxa Cash-out (%)</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={feeCashOutEnabled} onChange={(e) => setFeeCashOutEnabled(e.target.checked)} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500" />
                      <span className="text-xs text-surface-400">Habilitada</span>
                    </label>
                  </div>
                  <Input type="number" placeholder="0.00" value={feeCashOut} onChange={(e) => setFeeCashOut(e.target.value)} disabled={!feeCashOutEnabled} min="0" max="100" step="0.01" />
                </div>

                {/* Simulação Cash-in */}
                <div className="p-4 rounded-lg bg-surface-800/50 space-y-2">
                  <p className="text-sm font-medium text-surface-200 mb-3">Simulação Cash-in (R$ 100,00)</p>
                  <div className="flex justify-between text-sm text-surface-300"><span>Valor bruto:</span><span>R$ 100,00</span></div>
                  {feeAdhesionEnabled && (
                    <div className="flex justify-between text-sm text-surface-400"><span>Taxa de adesão:</span><span>- R$ {parseFloat(feeAdhesion || '0').toFixed(2)}</span></div>
                  )}
                  {feeCashInEnabled && (
                    <>
                      <div className="flex justify-between text-sm text-surface-400">
                        <span>Cash-in ({(parseFloat(feeCashIn || '0') * 100).toFixed(2)}%):</span>
                        <span>- R$ {(100 * parseFloat(feeCashIn || '0')).toFixed(2)}</span>
                      </div>
                      <div className="pl-4 space-y-1 border-l-2 border-surface-700 ml-2">
                        <div className="flex justify-between text-xs text-blue-400">
                          <span>↳ Fintech ({(parseFloat(feeFintechPct || '0') * 100).toFixed(2)}%):</span>
                          <span>R$ {(100 * parseFloat(feeFintechPct || '0')).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-orange-400">
                          <span>↳ Processador ({(parseFloat(feeProcessorPct || '0') * 100).toFixed(2)}%):</span>
                          <span>R$ {(100 * parseFloat(feeProcessorPct || '0')).toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-sm font-semibold text-surface-100 border-t border-surface-700 mt-2 pt-2">
                    <span>Valor líquido:</span>
                    <span className="text-green-400">R$ {(100 - (feeAdhesionEnabled ? parseFloat(feeAdhesion || '0') : 0) - (feeCashInEnabled ? 100 * parseFloat(feeCashIn || '0') : 0)).toFixed(2)}</span>
                  </div>
                </div>

                {/* Simulação Cash-out */}
                <div className="p-4 rounded-lg bg-surface-800/50 space-y-2">
                  <p className="text-sm font-medium text-surface-200 mb-3">Simulação Cash-out (R$ 100,00)</p>
                  <div className="flex justify-between text-sm text-surface-300"><span>Valor do envio:</span><span>R$ 100,00</span></div>
                  {feeCashOutEnabled && (
                    <div className="flex justify-between text-sm text-surface-400">
                      <span>Cash-out ({feeCashOut}%):</span>
                      <span>- R$ {(100 * parseFloat(feeCashOut || '0') / 100).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-semibold text-surface-100 border-t border-surface-700 mt-2 pt-2">
                    <span>Total debitado:</span>
                    <span className="text-red-400">R$ {(100 + (feeCashOutEnabled ? 100 * parseFloat(feeCashOut || '0') / 100 : 0)).toFixed(2)}</span>
                  </div>
                </div>

                <Button onClick={handleSaveFeeConfig} disabled={updateFeeConfig.isPending} className="w-full">
                  {updateFeeConfig.isPending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salvar Configuração
                </Button>
              </div>
            )}
          </Card>
        </motion.div>
      )}

      {/* Transaction Detail Modal */}
      <Modal isOpen={!!selectedTx} onClose={() => setSelectedTx(null)} title="Detalhes da Transação" size="lg">
        {selectedTx && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-surface-500">Tipo</p>
                <p className="text-surface-100">{getTypeLabel(selectedTx.type, selectedTx.status)}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500">Status</p>
                <Badge variant={selectedTx.status === 'COMPLETED' ? 'success' : selectedTx.status === 'PENDING' ? 'warning' : 'error'}>
                  {statusLabels[selectedTx.status] || selectedTx.status}
                </Badge>
              </div>
            </div>

            {/* Amount breakdown */}
            <div className="p-4 bg-surface-800 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-surface-400">Valor Bruto</span>
                <span className="text-surface-100 font-semibold">{formatCurrency(Number(selectedTx.grossAmount))}</span>
              </div>
              {selectedTx.totalFees > 0 && (
                <div className="flex justify-between text-yellow-400">
                  <span>Total de Taxas</span>
                  <span>- {formatCurrency(Number(selectedTx.totalFees))}</span>
                </div>
              )}
              {selectedTx.providerFee && selectedTx.providerFee > 0 && (
                <div className="flex justify-between text-surface-400 text-sm">
                  <span>Taxa Provider (AtivoPay)</span>
                  <span>{formatCurrency(Number(selectedTx.providerFee))}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-surface-700 pt-2">
                <span className="text-surface-300 font-medium">Valor Líquido</span>
                <span className="text-surface-100 font-bold text-lg">{formatCurrency(Number(selectedTx.netAmount))}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <p className="text-sm text-surface-500">ID</p>
                <p className="text-surface-100 font-mono text-sm break-all">{selectedTx.correlationId}</p>
              </div>
              {selectedTx.customer && (
                <div className="col-span-2 p-3 bg-surface-800/50 rounded-lg">
                  <p className="text-sm text-surface-400 mb-1 flex items-center gap-1"><ArrowDownLeft className="w-3 h-3" /> Cliente (Pagador)</p>
                  <p className="text-surface-100 font-medium">{selectedTx.customer.name}</p>
                  {selectedTx.customer.document && <p className="text-xs text-surface-400">CPF/CNPJ: {selectedTx.customer.document}</p>}
                  {selectedTx.customer.email && selectedTx.customer.email !== '00000000000' && <p className="text-xs text-surface-400">{selectedTx.customer.email}</p>}
                </div>
              )}
              {selectedTx.beneficiary && (
                <div className="col-span-2 p-3 bg-surface-800/50 rounded-lg">
                  <p className="text-sm text-surface-400 mb-1 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> Beneficiário (Saque)</p>
                  <p className="text-surface-100 font-medium">{selectedTx.beneficiary.name}</p>
                  {selectedTx.beneficiary.document && <p className="text-xs text-surface-400">CPF/CNPJ: {selectedTx.beneficiary.document}</p>}
                  {selectedTx.beneficiary.pixKey && <p className="text-xs text-surface-400">Chave PIX: {selectedTx.beneficiary.pixKey} ({selectedTx.beneficiary.pixType})</p>}
                </div>
              )}
              {selectedTx.endToEndId && (
                <div className="col-span-2">
                  <p className="text-sm text-surface-500">End-to-End ID</p>
                  <p className="text-surface-100 font-mono text-sm break-all">{selectedTx.endToEndId}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-surface-500">Criada em</p>
                <p className="text-surface-100">{formatDateTime(selectedTx.createdAt)}</p>
              </div>
              {selectedTx.paidAt && (
                <div>
                  <p className="text-sm text-surface-500">Pago em</p>
                  <p className="text-surface-100 text-green-400">{formatDateTime(selectedTx.paidAt)}</p>
                </div>
              )}
            </div>
            {selectedTx.description && (
              <div>
                <p className="text-sm text-surface-500">Descrição</p>
                <p className="text-surface-100">{selectedTx.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
